import React, { useContext, useEffect, useState } from "react";
import { Undo } from "@mui/icons-material";
import { AlertTitle, Box, Button, Divider, Paper, Typography } from "@mui/material";
import { Feature } from "geojson";
import { uniqBy } from "lodash";

import { LoadingButton } from "ui";

import { AsyncOperationState } from "fond/async/redux";
import { HistoryContext, useHistory } from "fond/history";
import { inputLayerGroups } from "fond/layers";
import MapOverlay from "fond/map/MapOverlay";
import { MapContext } from "fond/map/MapProvider";
import { featureCollection } from "fond/turf";
import { isValuePoint } from "fond/types/geojson";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { BlockSpinner, ConfirmModal, useStackedNavigationContext } from "fond/widgets";
import StackedNavigationHeader from "fond/widgets/StackedNavigation/StackedNavigationHeader";

import { closeEditing, EditMode, getUploadStatus, saveEditing, setEditMode } from "../redux";

import { Alert } from "./InputDataPanel.styles";

const Draw: React.FC = () => {
  const dispatch = useAppDispatch();
  const { clear: clearHistory } = useHistory(HistoryContext);
  const { drawControl, isDirty: isDrawingDirty, setDrawMode } = useContext(MapContext);
  const inputLayerGroupId = useAppSelector((state) => state.project.editingLayerGroupId);
  const { data } = useAppSelector((state) => state.project.projects[state.project.projectId]);
  const [showConfirm, setShowConfirm] = useState<"revert" | "discard" | null>(null);
  const editMode = useAppSelector((state) => state.project.editMode);
  const { goBack, clear } = useStackedNavigationContext();
  const layerGroup = inputLayerGroups[inputLayerGroupId];
  const uploadStatus = useAppSelector((state) => getUploadStatus(state.project, inputLayerGroupId));

  useEffect(() => {
    if (editMode === EditMode.none) {
      clear();
    }
  }, [clear, editMode]);

  const handleBack = () => {
    if (isDrawingDirty) {
      setShowConfirm("discard");
    } else {
      setDrawMode("no_feature");
      dispatch(closeEditing());
      goBack();
    }
  };

  const handleOnConfirmDiscard = () => {
    setDrawMode("no_feature");
    dispatch(setEditMode(EditMode.none));
  };

  const handleOnConfirmRevert = () => {
    clearHistory();
    setShowConfirm(null);
  };

  const onImport = async () => {
    const layerGroupIds = inputLayerGroups[inputLayerGroupId].layers.map(({ id }) => id);
    let features: Feature[] = layerGroupIds.reduce(
      (currFeatures: Feature[], layerId) => [
        ...currFeatures,
        ...(drawControl.current?.getAll().features.filter((f) => f.properties?.layerId === layerId) || []),
      ],
      []
    );

    // When editing hubs we only edit a portion of all existing hubs, however we need
    // to pass the entire hub feature list otherwise backend will report an error
    if (inputLayerGroupId === "hub") {
      features = uniqBy([...features, ...(data?.layers?.["output/hub"]?.features ?? [])], "id");
      features = features.map((f) => ({ ...f, id: f.properties?.featureId }));
    }

    // Workaround https://github.com/mapbox/mapbox-gl-draw/issues/774
    features = features.filter(isValuePoint);

    await dispatch(saveEditing(featureCollection(features), [], "draw-panel"));
    clear();

    if (isDrawingDirty) {
      setDrawMode("no_feature");
    }

    dispatch(closeEditing());
  };

  const handleOnReset = () => {
    if (isDrawingDirty) {
      setShowConfirm("revert");
    }
  };

  const layerGroupTitle = {
    [inputLayerGroups.inAddress.id]: "Draw addresses",
    [inputLayerGroups.inBaseDataStreet.id]: "Draw streets",
    [inputLayerGroups.inStreet.id]: "Draw underground path",
    [inputLayerGroups.inExchange.id]: "Draw central offices",
    [inputLayerGroups.spanPole.id]: "Draw spans & poles",
    [inputLayerGroups.hub.id]: "Move hubs to your preferred location",
    [inputLayerGroups.inLeadIns.id]: "Edit Lead Ins",
    [inputLayerGroups.inUnderground.id]: "Edit UG Paths & Points",
  };

  return (
    <>
      <StackedNavigationHeader onBackButtonClick={handleBack} title="Draw on map" />
      <Box data-testid="draw-panel" padding={1}>
        <Alert severity="info" data-testid="layer-alert">
          <AlertTitle sx={{ mb: 0 }}>{layerGroupTitle[layerGroup.id]}</AlertTitle>
        </Alert>
        <Divider sx={{ mt: 3 }} />
        <Button
          fullWidth
          sx={{ my: 0.5, justifyContent: "left" }}
          startIcon={<Undo sx={{ height: 20 }} />}
          onClick={handleOnReset}
          disabled={!isDrawingDirty}
        >
          <Typography color={(theme) => theme.palette.common.black} fontWeight={500} fontSize={13}>
            Reset
          </Typography>
        </Button>
        <Divider />
        <Box display="flex" alignItems="center" justifyContent="flex-end" mt={2}>
          <Button color="primary" size="small" onClick={handleBack} disabled={uploadStatus === AsyncOperationState.executing}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            size="small"
            onClick={onImport}
            sx={{ ml: 1, px: 2 }}
            data-testid="finish-button"
            disabled={!isDrawingDirty}
            loading={uploadStatus === AsyncOperationState.executing}
          >
            Finish
          </LoadingButton>
        </Box>
      </Box>

      <ConfirmModal
        open={!!showConfirm}
        content="Are you sure you want to discard your changes?"
        onConfirm={showConfirm === "discard" ? handleOnConfirmDiscard : handleOnConfirmRevert}
        onCancel={() => {
          setShowConfirm(null);
        }}
        data-testid="confirm-revert"
      />

      {uploadStatus === AsyncOperationState.executing && (
        <MapOverlay>
          <Paper sx={{ py: 1, px: 3, textAlign: "center", width: 200 }}>
            <BlockSpinner spinnerProps={{ size: 20 }} />
            <Typography component="p" variant="caption" pt={1}>
              Saving changes...
            </Typography>
          </Paper>
        </MapOverlay>
      )}
    </>
  );
};

export default Draw;
